/** Hooks for managing per-group invite links (`group_invites` table). */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { mapSupabaseError } from '@/lib/errorMapper';
import { assertOnline } from '@/lib/networkGuard';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';

/** Shape of a row from the `group_invites` table. */
export interface GroupInvite {
  id: string;
  /** The shareable 8-character hex code embedded in invite URLs. */
  invite_code: string;
  group_id: string;
  /** User ID of the member who generated this invite. */
  created_by: string;
  created_at: string;
  /** ISO timestamp after which the invite can no longer be used. */
  expires_at: string;
  /** Set when a group admin explicitly revokes the invite; null while active. */
  revoked_at: string | null;
  /** Optional item ID attached to the invite — shown to the invitee on landing. */
  item_id: string | null;
}

/**
 * Returns all currently active (non-revoked, non-expired) invite links for a group.
 *
 * Each group can have multiple active invites simultaneously. An invite is
 * considered active when `revoked_at IS NULL` and `expires_at > now()`.
 *
 * The `as any` cast is required because the `group_invites` table is not yet
 * included in the auto-generated Supabase TypeScript types.
 *
 * @param groupId - The UUID of the group whose invites to fetch.
 * @returns A TanStack `UseQueryResult` resolving to an array of {@link GroupInvite} objects.
 */
export function useGroupInvites(groupId: string) {
  return useQuery({
    queryKey: ['group-invites', groupId],
    queryFn: async (): Promise<GroupInvite[]> => {
      const { data, error } = await supabase
        // [LEARN] "as any" bypasses TypeScript type checking — needed here because group_invites
        // isn't in the auto-generated Supabase types yet, so TS would reject the table name.
        .from('group_invites' as any)
        .select('*')
        .eq('group_id', groupId)
        .is('revoked_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as any[]) || [];
    },
    // Skip query when groupId is absent
    enabled: !!groupId,
  });
}

/**
 * Generates a new invite link for a group via the `generate_group_invite` RPC.
 *
 * The RPC sets an `expires_at` timestamp and returns the new invite details.
 * Rate-limited to 20 invites per user per group per 24 hours (DB-enforced).
 * The invite code is intentionally omitted from the analytics event to avoid
 * logging shareable tokens.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts `{ groupId: string; itemId?: string }`.
 * @throws Supabase error if the rate limit is exceeded or the user lacks group membership.
 */
export function useGenerateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, itemId }: { groupId: string; itemId?: string }) => {
      await assertOnline();
      const { data, error } = await supabase.rpc('generate_group_invite' as any, {
        p_group_id: groupId,
        ...(itemId ? { p_item_id: itemId } : {}),
      });
      if (error) throw error;
      return data as {
        id: string;
        invite_code: string;
        expires_at: string;
        created_at: string;
        item_id: string | null;
      };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['group-invites', variables.groupId] });
      // Intentionally omit invite_code from analytics — it is a shareable secret.
      posthog.capture('invite_generated', { group_id: variables.groupId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Revokes an invite link by calling the `revoke_group_invite` RPC.
 *
 * Sets `revoked_at` on the row, making the invite unusable even if
 * `expires_at` has not yet passed.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts `{ inviteId: string; groupId: string }`.
 */
export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, groupId }: { inviteId: string; groupId: string }) => {
      await assertOnline();
      const { error } = await supabase.rpc('revoke_group_invite' as any, {
        p_invite_id: inviteId,
      });
      if (error) throw error;
    },
    onSuccess: (_, { inviteId, groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['group-invites', groupId] });
      posthog.capture('invite_revoked', { group_id: groupId, invite_id: inviteId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}
